<template>
    <div>
        <ul class="uk-subnav uk-subnav-line">
            <li :class="{'uk-active': !state.status}"><a href @click.prevent="filter_by_field('', 'status')">All</a></li>
            <li
                v-for="status in available_project_status"
                :key="status.id"
                :class="{'uk-active': (state.status === (status.code_constant||status.id)) }"
            >
                <a href @click.prevent="filter_by_field(status.code_constant||status.id, 'status')">{{ status.name }} ({{ status.additional_service_usage_count || 0 }})</a>
            </li>
        </ul>

        <data-table
            id="prospective-projects"
            :columns="visible_columns"
            :rows="filtered_rows"
            :search="state.search"
            :total="state.total"
            :found="state.found"
            :current-page="state.page"
            :total-pages="nb_pages"
            :searchable="false"
            table-class="lam-records-list"
            :row-class-provider="row_classes"
            empty-title="No projects found"
            :export-entity="entity"
            :export-query="state"
            :export-fields="export_fields"
            @sort="sort_by"
            @search="search"
            @page="goto_page"
            @click="record_edit('project', $event.project_id)"
        >
            <template v-slot:controls>
                <div>
                    <base-button
                        flavor="link"
                        @click.prevent="toggle_filters(true)"
                    >
                        <span v-html="applied_filters"></span>
                    </base-button>
                    <base-button
                        v-if="!!filter_description"
                        flavor="link"
                        @click.prevent="clear_filters"
                        class="uk-margin-left"
                    >
                        <open-icon glyph="close" />
                    </base-button>
                    <base-button
                        v-if="!!templates.length"
                        flavor="link"
                        @click.native="picking_template=!picking_template"
                    >
                        <open-icon glyph="list-ul" />
                    </base-button>
                </div>
            </template>
            <template v-slot:mid-controls>
                <div>
                    <form-select-user
                        id="pic_id_filter"
                        name="pic_id_filter"
                        :value="state.pic_id"
                        :editable="true"
                        empty-label="(All PICs)"
                        :empty-value="0"
                        :required="false"
                        @input="filter_by_field($event, 'pic_id')"
                    />
                </div>
            </template>
            <template
                v-slot:summary="{ row }"
            >
                <h6>{{ row.name }}</h6>
            </template>
            <template
                v-slot:tbody="{ row, field }"
            >
                <router-link
                    v-if="field === 'name'"
                    :to="{ name: 'project.edit', params: { id: row.project_id } }"
                >
                    {{ project_field_value_for_row(row, "name") }}
                    <span
                        v-if="!!project_field_value_for_row(row, 'other_teams')"
                        class="uk-margin-small-left"
                    >
                        <open-icon glyph="group" title="Other Teams" />
                    </span>
                    <span
                        v-if="!project_field_value_for_row(row, 'proposal_amount')"
                        class="uk-text-warning uk-margin-small-left"
                    >
                        <open-icon glyph="file-pdf-o" title="Proposal not written" />
                    </span>
                </router-link>
                <span v-else-if="field === 'sortable_number'">{{ project_field_value_for_row(row, "sortable_number") | lam.project-number }}</span>
                <span v-else-if="field === 'computed_fee'">$&nbsp;{{ row[field] | nibnut.number("0,0") }}</span>
                <div v-else-if="field === 'computed_starts_at_as_string'">
                    <span
                        v-if="!project_field_value_for_row(row, field)"
                        class="uk-text-danger"
                    >
                        <open-icon glyph="calendar-times-o" />
                    </span>
                    <span v-else>{{ project_field_value_for_row(row, field) | nibnut.date("MM/YYYY") }}</span>
                </div>
                <div v-else-if="field === 'sent_at'">
                    <span
                        v-if="!row[field]"
                        class="uk-text-danger"
                    >
                        <open-icon glyph="calendar-times-o" />
                    </span>
                    <span v-else>{{ row[field] | nibnut.date("MM/YYYY") }}</span>
                </div>
                <div v-else-if="field === 'computed_last_communication_at_as_string'">
                    <span
                        v-if="!row[field]"
                        class="uk-text-danger"
                    >
                        <open-icon glyph="calendar-times-o" />
                    </span>
                    <span v-else>{{ row[field] | nibnut.date("MM/DD/YY") }}</span>
                </div>
                <span v-else-if="field === 'days_since_received'">
                    <span v-if="row[field] >= 60">{{ (row[field] / 30) | nibnut.number("0,0") }}&nbsp;months</span>
                    <span v-else>{{ row[field] | nibnut.number("0,0") }}&nbsp;days</span>
                </span>
                <div
                    v-else-if="field === 'automated_communication_log_count'"
                >
                    <button
                        v-if="(project_field_value_for_row(row, 'recent_user_note_count') + project_field_value_for_row(row, 'automated_communication_log_count')) > 0"
                        type="button"
                        :class="{ 'uk-button-primary-w-icon': !!project_field_value_for_row(row, 'automated_communication_log_count') && !project_field_value_for_row(row, 'recent_user_note_count'), 'uk-button-danger': !!project_field_value_for_row(row, 'recent_user_note_count') }"
                        class="uk-button"
                        @click.prevent.stop="display_logs_for_project(row)"
                    >
                        <open-icon glyph="sticky-note" />
                    </button>
                </div>
                <span v-else-if="field.match(/^((pic|pm)_initials|client_name|active_phase|status_label)$/i)">{{ project_field_value_for_row(row, field) }}</span>
                <span v-else>{{ row[field] }}</span>
            </template>
        </data-table>

        <projects-list-filter-dialog
            :show.sync="showing_filters"
            :state="state"
            :entity="entity"
            :prospective="prospective"
            :stateUi="state_ui"
            :available-project-status="available_project_status"
            :size-unit-html="size_unit_html"
            @filter="filter_by_field"
            @pick-firm="pick_firm"
            @units-changed="units_changed"
        >
            <template v-slot:pick-template-button>
                <span v-if="!!templates.length"  class="uk-margin-left">
                    <base-button size="mini" @click.native="picking_template=!picking_template">
                        <open-icon glyph="list-ul" />
                    </base-button>
                </span>
            </template>
            <template v-slot:templates-controls>
                <templates-controls
                    :template="current_template"
                    :entity="entity"
                    category="filters"
                    :savable="template_savable"
                    :dirty="template_dirty"
                    :clone="template_to_clone"
                    @save="template_editor_save"
                    @stop-editing="template_editor_close"
                />
            </template>
        </projects-list-filter-dialog>
        <project-logs-dialog
            :project="viewing_logs_for_project || {}"
            :show.sync="viewing_logs"
        />
        <template-picker
            :template="current_template"
            :entity="entity"
            :active="picking_template"
            title="Saved Filters"
            category="filters"
            @close="picking_template=false"
            @clone="template_clone"
            @load="template_load"
        />
    </div>
</template>

<script type="text/javascript">
import { mapGetters } from "vuex"

import is_projects_list from "./IsProjectsList"

import FormSelectUser from "../../../ui/FormSelectUser"

export default {
    mixins: [is_projects_list],
    components: {
        FormSelectUser
    },
    methods: {
        row_classes (row) {
            const old = row.computed_last_communication_at_as_string ? (this.$moment().diff(this.$moment(row.computed_last_communication_at_as_string), "days") >= 30) : false
            return { "uk-text-danger": old }
        },
        project_field_value_for_row (row, field) {
            const project = this.entity_record("project", row.project_id)
            return project ? project[field] : null
        }
    },
    computed: {
        ...mapGetters(["entity_records", "entity_record"]),
        state_identifier () {
            return "service_request.list"
        },
        fields () {
            return ["fieldset::list", "ns::project;fieldset::sublist"]
        },
        visible_columns () {
            const columns = {}

            columns.pic_initials = this.columns.pic_initials
            columns.pm_initials = this.columns.pm_initials
            columns.sortable_number = this.columns.sortable_number
            columns.name = this.columns.name
            columns.client_name = this.columns.client_name
            columns.active_phase = this.columns.active_phase
            columns.shelf_life = this.columns.shelf_life
            columns.days_since_received = this.columns.days_since_received
            columns.computed_fee = this.columns.computed_fee
            columns.sent_at = this.columns.sent_at
            columns.computed_last_communication_at_as_string = this.columns.computed_last_communication_at_as_string
            columns.computed_starts_at_as_string = this.columns.computed_starts_at_as_string // specific to this service????
            columns.automated_communication_log_count = this.columns.automated_communication_log_count

            return columns
        },
        available_project_status () {
            const statii = this.dictionary(this.$root.constants.dictionaries.SHELF_LIFE, "name").filter((term) => {
                if(!!term && !!term.code_constant) return true
                return false
            })
            return statii
        }
    },
    data () {
        return {
            entity: "service_request",
            relation_ids: ["project"],
            prospective: true,
            addl_export_fields: [
                "number", "computed_fee", "hours", "computed_spent", "target_lpd", "lighting_costs", "controls_costs",
                "package", "consider_for_marketing", "pro_bono", "activated_at_as_string", "completed_at_as_string",
                "marketing_sheets_created", "marketing_photos", "web_published", "active_phase_constant", "project_sustainability_programs_label"
            ]
        }
    }
}
</script>
